/**
 * **이 앱의 달력은 한국 시간(KST)이다 — 날짜·시각을 적고 옮기는 한 곳.** 의존성 없는 순수 모듈이다(Vue·`@/` 별칭 금지 —
 * 단위 테스트가 브라우저 시간대를 바꿔 가며 node 에서 그대로 읽는다).
 *
 * 서버·DB·매장 운영 시각은 KST 다(주문 조회 기간·통계 날짜·쿠폰 만료일·임시중단 시각). 그런데 화면이 「오늘」을 브라우저의
 * `new Date()` 로컬 칸(`getDate()` 등)으로 적으면 **보는 사람의 시간대**가 끼어든다 — KST 가 아닌 브라우저(해외 출장·
 * 원격 근무, R-K2 J7·K4/K5 O-4 실측 America/Edmonton)에서는 기본 조회 기간이 하루 앞서 KST 오늘 주문이 안 보이고,
 * `new Date('YYYY-MM-DD')`(UTC 자정)를 로컬 칸으로 읽은 쿠폰 만료일은 하루 모자랐다.
 *
 * 규칙은 하나다: **순간(ms)은 KST 벽시계로 읽고 적는다.** KST 는 서머타임이 없어 +9시간 고정이다 — 순간에 9시간을 더한
 * `Date` 의 UTC 칸이 곧 KST 벽시계다. 시계를 읽는 것(`Date.now()`)은 부르는 쪽(경계)이 한다.
 */

/** 한국 표준시의 UTC 오프셋 — 서머타임 없음 */
export const KST_OFFSET_MS = 9 * 60 * 60 * 1000

/** 순간 → KST 벽시계를 UTC 칸에 담은 `Date`(내부 전용 — 로컬 칸으로 읽으면 안 된다) */
const kstClock = (ms) => new Date(ms + KST_OFFSET_MS)

/**
 * 순간의 KST 벽시계 칸. 순수.
 *
 * @param {number} ms 순간(epoch ms)
 * @returns {{year: number, month: number, day: number, hour: number, minute: number, second: number, millisecond: number}}
 *   `month` 는 1~12
 */
export function kstParts(ms) {
  const clock = kstClock(ms)

  return {
    year: clock.getUTCFullYear(),
    month: clock.getUTCMonth() + 1,
    day: clock.getUTCDate(),
    hour: clock.getUTCHours(),
    minute: clock.getUTCMinutes(),
    second: clock.getUTCSeconds(),
    millisecond: clock.getUTCMilliseconds(),
  }
}

/**
 * **순간을 KST 로 적는다** — `util.getDate`·`util.getNow` 의 형식 문법 그대로(`y+` 연 · `M+` 월 · `d+` 일 · `H+` 시(24) ·
 * `m+` 분 · `s+` 초 · `S` 밀리초). 칸이 한 글자면 채우지 않고, 두 글자 이상이면 두 자리로 채운다. 순수.
 *
 * @param {string} fmt 예: `'yyyy-MM-dd HH:mm:ss'`
 * @param {number} ms 순간(epoch ms)
 * @returns {string}
 */
export function formatKst(fmt, ms) {
  const parts = kstParts(ms)
  const fields = [
    ['M+', parts.month],
    ['d+', parts.day],
    ['H+', parts.hour],
    ['m+', parts.minute],
    ['s+', parts.second],
    ['S', parts.millisecond],
  ]

  const withYear = fmt.replace(/(y+)/, (years) => String(parts.year).substr(4 - years.length))

  return fields.reduce(
    (text, [pattern, value]) =>
      text.replace(new RegExp('(' + pattern + ')'), (token) =>
        token.length === 1 ? String(value) : String(value).padStart(2, '0')
      ),
    withYear
  )
}

/**
 * **서버가 준 시각 글자(ISO-8601 — 보통 `+09:00`)를 KST 로 적는다** — 형식은 [formatKst] 와 같다. 읽을 수 없으면(빈 값·형식 밖)
 * `null` 이다 — 「모름」을 어떻게 말할지는 부르는 쪽이 정한다. 순수.
 *
 * @param {string} fmt 예: `'M월 d일 HH:mm'`
 * @param {?string} iso 예: `'2026-10-02T21:12:30+09:00'`
 * @returns {?string}
 */
export function formatKstIso(fmt, iso) {
  const ms = typeof iso === 'string' && iso ? Date.parse(iso) : NaN
  return Number.isNaN(ms) ? null : formatKst(fmt, ms)
}

/**
 * **KST 달력으로 순간을 옮긴다** — `util.dateAdd` 의 간격 어휘 그대로(`'y '` 연 · `'q '` 분기 · `'m '` 월 · `'w '` 주 ·
 * `'d '` 일 · `'h '` 시 · `'M '` 분 · `'s '` 초, 그 밖은 일). 월·연 넘침은 `Date` 의 규칙 그대로(1/31 + 1개월 = 3/3). 순수.
 *
 * @param {string} interval
 * @param {number} number 옮길 양(음수면 과거)
 * @param {number} ms 기준 순간(epoch ms)
 * @returns {number} 옮긴 순간(epoch ms)
 */
export function shiftKst(interval, number, ms) {
  const clock = kstClock(ms)
  const shift = SHIFTS[interval] || SHIFTS['d ']

  shift(clock, number)

  return clock.getTime() - KST_OFFSET_MS
}

/** 간격 → KST 칸(UTC 칸에 담긴)을 옮기는 법 — `shiftKst` 가 만든 제 사본만 바꾼다 */
const SHIFTS = Object.freeze({
  'y ': (clock, n) => clock.setUTCFullYear(clock.getUTCFullYear() + n),
  'q ': (clock, n) => clock.setUTCMonth(clock.getUTCMonth() + n * 3),
  'm ': (clock, n) => clock.setUTCMonth(clock.getUTCMonth() + n),
  'w ': (clock, n) => clock.setUTCDate(clock.getUTCDate() + n * 7),
  'd ': (clock, n) => clock.setUTCDate(clock.getUTCDate() + n),
  'h ': (clock, n) => clock.setUTCHours(clock.getUTCHours() + n),
  'M ': (clock, n) => clock.setUTCMinutes(clock.getUTCMinutes() + n),
  's ': (clock, n) => clock.setUTCSeconds(clock.getUTCSeconds() + n),
})

/**
 * **KST 오늘을 이 브라우저의 자정 `Date` 로** — 로컬 칸(`getDate`·`setDate`·`getDay`·`new Date(y, m, 1)`)으로 달력을 셈하는
 * 코드(기간 프리셋 `periodFilter`)가 그대로 KST 날짜를 얻게 한다. 날짜만 담는다(시각은 자정) — 순간으로 쓰지 않는다. 순수.
 *
 * @param {number} ms 지금(epoch ms)
 * @returns {Date}
 */
export function kstTodayLocal(ms) {
  const { year, month, day } = kstParts(ms)

  return new Date(year, month - 1, day)
}

/**
 * **달력 날짜(`YYYY-MM-DD`)에 날수를 더한다** — 시간대와 무관한 날짜 셈이다(순간을 거치지 않는다). 순수.
 *
 * `new Date('YYYY-MM-DD')` 는 UTC 자정이라 로컬 칸으로 읽으면 음(−) 오프셋 브라우저에서 하루 앞선다 — 쿠폰 발송 창의 만료일이
 * 그렇게 하루 모자랐다(K4·K5 O-4).
 *
 * @param {string} ymd `YYYY-MM-DD`
 * @param {number} days 더할 날수(음수 가능)
 * @returns {string} `YYYY-MM-DD`, 읽을 수 없는 입력이면 `''`
 */
export function addCalendarDays(ymd, days) {
  const parts = calendarParts(ymd)
  if (!parts) return ''

  return ymdOf(new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days)))
}

/**
 * **달력 날짜(`YYYY-MM-DD`)를 달 단위로 옮긴다 — 말일을 넘치지 않는다**(3/31 의 전월은 2/28, 윤년 2/29 의 전년은 2/28).
 * 비교 기간(전월 대비·전년 동기)의 셈이다 — 넘침 규칙(1/31 + 1개월 = 3/3)을 보존하는 [shiftKst] 와 다르다. 시간대와 무관하다
 * (UTC 칸만 쓴다). 순수.
 *
 * @param {string} ymd `YYYY-MM-DD`
 * @param {number} months 옮길 달 수(음수면 과거)
 * @returns {string} `YYYY-MM-DD`, 읽을 수 없는 입력이면 `''`
 */
export function addCalendarMonths(ymd, months) {
  const parts = calendarParts(ymd)
  if (!parts) return ''

  const lastDay = new Date(Date.UTC(parts.year, parts.month - 1 + months + 1, 0)).getUTCDate()

  return ymdOf(new Date(Date.UTC(parts.year, parts.month - 1 + months, Math.min(parts.day, lastDay))))
}

/**
 * **두 달력 날짜 사이의 날수**(`end - start`) — 시간대와 무관하다. 순수.
 *
 * @returns {number} 읽을 수 없는 입력이면 `NaN`
 */
export function calendarDaysBetween(startYmd, endYmd) {
  const start = calendarParts(startYmd)
  const end = calendarParts(endYmd)
  if (!start || !end) return NaN

  const DAY_MS = 24 * 60 * 60 * 1000

  return Math.round(
    (Date.UTC(end.year, end.month - 1, end.day) - Date.UTC(start.year, start.month - 1, start.day)) / DAY_MS
  )
}

/** `YYYY-MM-DD` → 달력 칸(`month` 1~12), 읽을 수 없으면 `null` */
function calendarParts(ymd) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''))

  return match ? { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) } : null
}

/** UTC 칸의 날짜 → `YYYY-MM-DD` */
function ymdOf(date) {
  return [date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate()]
    .map((value, index) => String(value).padStart(index === 0 ? 4 : 2, '0'))
    .join('-')
}
